<template>
  <div class="card-body pt-0 edge-to-edge" v-if="$gate.isAdmin()">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h5 class="mb-0">Extrase bancare</h5>
      <div class="d-flex align-items-center gap-2">
        <button v-if="selectedExtras" type="button" class="btn btn-sm btn-primary-bright" @click="showAddLines = !showAddLines">
          <i class="fa fa-plus me-1 text-white"></i>
          Adaugă linii
        </button>
        <button type="button" class="btn btn-sm btn-light-primary" @click="reload">
          <i class="fa fa-rotate-right me-1"></i>
          Reîncarcă
        </button>
      </div>
    </div>

    <div v-if="loading && !selectedExtras" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Se încarcă...</span>
      </div>
      <div class="text-muted mt-2">Se încarcă extrasele...</div>
    </div>

    <div class="table-responsive" v-show="!selectedExtras && !loading">
      <table class="table table-bordered table-striped align-middle" id="tabel_extrase_bancare">
        <thead>
          <tr class="text-uppercase fw-bold">
            <th style="width: 40px" class="text-center">#</th>
            <th>Banca</th>
            <th>IBAN</th>
            <th>Monedă</th>
            <th>Perioadă</th>
            <th class="text-end">Sold inițial</th>
            <th class="text-end">Sold final</th>
            <th class="text-center">Stare</th>
            <th style="width: 120px" class="text-center">Acțiuni</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(extras, index) in extrase" :key="extras.id">
            <td class="text-center">{{ index + 1 }}</td>
            <td>{{ extras.banca || '-' }}</td>
            <td>{{ extras.iban || '-' }}</td>
            <td>{{ extras.moneda || 'RON' }}</td>
            <td>{{ formatLunaAn(extras.data_start, extras.data_end) || '-' }}</td>
            <td class="text-end">{{ formatMoney(extras.sold_initial) }}</td>
            <td class="text-end">{{ formatMoney(extras.sold_final) }}</td>
            <td class="text-center">
              <div>
                <span
                  class="badge"
                  :class="extras.procesat ? 'bg-success' : 'bg-secondary'"
                >
                  {{ extras.procesat ? 'Procesat' : 'Neprocesat' }}
                </span>
              </div>
              <div
                v-if="typeof extras.total_linii !== 'undefined'"
                class="mt-1 small"
              >
                <span class="badge bg-success me-1" title="Linii procesate">
                  {{ extras.linii_procesate }}
                </span>
                <span class="badge bg-danger me-1" title="Linii neprocesate">
                  {{ extras.linii_neprocesate }}
                </span>
                <span class="badge bg-secondary" title="Total linii">
                  {{ extras.total_linii }}
                </span>
              </div>
            </td>
            <td class="text-center">
              <button
                type="button"
                class="btn btn-sm btn-light-primary"
                @click="selectExtras(extras)"
              >
                <i class="fa-solid fa-book me-1"></i>
                Lucrează
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Secțiune lucru pe extras selectat -->
    <div v-if="selectedExtras" class="mt-5">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="mb-0">
          Lucrezi pe extras:
          <strong>{{ selectedExtras.banca || '-' }}</strong>
          <span class="text-muted ms-2">
            ({{ selectedExtras.iban || '-' }})
            <span v-if="selectedExtras.data_start || selectedExtras.data_end">
              -
              {{ formatDate(selectedExtras.data_start) || '-' }}
              -
              {{ formatDate(selectedExtras.data_end) || '-' }}
            </span>
          </span>
        </h6>
        <button type="button" class="btn btn-sm btn-light" @click="clearSelectedExtras">
          <i class="fa fa-arrow-left me-1"></i>
          Înapoi la lista de extrase
        </button>
      </div>

      <!-- Secțiune adăugare linii noi (mutată sus) -->
      <div v-show="showAddLines" class="card card-body p-3 mb-4 border-dashed bg-light-primary bg-opacity-10" v-if="!loadingLinii">
          <div class="d-flex align-items-center justify-content-between mb-3">
              <h6 class="mb-0 fw-bold">Adaugă linii noi în extras</h6>
              <button
                  type="button"
                  class="btn btn-light-primary btn-sm"
                  @click="liniiRJ.push({ data_tranzactie: dateAzi(), suma_debit: 0, suma_credit: 0, cont_corespondent: '', cont_corespondent_obj: null, explicatie: '' })"
              >
                  <i class="fa-solid fa-plus me-1"></i> Adaugă rând
              </button>
          </div>

          <div v-if="liniiRJ.length" class="table-responsive">
              <table class="table table-sm table-bordered align-middle mb-3">
                  <thead class="table-light">
                      <tr>
                          <th style="width:140px">Data</th>
                           <th style="width:240px" class="text-end">Debit (Suma)</th>
                           <th style="width:240px" class="text-end">Credit (Suma)</th>
                           <th style="width:400px">Cont Corespondent</th>
                          <th>Explicație</th>
                          <th style="width:40px"></th>
                      </tr>
                  </thead>
                  <tbody>
                      <tr v-for="(linie, idx) in liniiRJ" :key="`rj_new_${idx}`">
                          <td>
                              <div class="input-group input-group-sm">
                                  <input
                                      type="date"
                                      class="form-control form-control-sm px-1"
                                      v-model="linie.data_tranzactie"
                                      :ref="'data_linie_new_' + idx"
                                  />
                                  <button
                                      type="button"
                                      class="date-picker-btn btn btn-outline-secondary btn-sm px-1"
                                      @click="openNativePicker('data_linie_new_' + idx)"
                                  >
                                      📅
                                  </button>
                              </div>
                          </td>
                          <td>
                              <input
                                  type="number"
                                  step="0.01"
                                  class="form-control form-control-sm text-end"
                                  v-model.number="linie.suma_debit"
                              />
                          </td>
                          <td>
                              <input
                                  type="number"
                                  step="0.01"
                                  class="form-control form-control-sm text-end"
                                  v-model.number="linie.suma_credit"
                              />
                          </td>
                          <td>
                              <multiselect
                                  v-model="linie.cont_corespondent_obj"
                                  :options="listaConturi"
                                  track-by="id"
                                  label="cod_complet"
                                  :custom-label="contLabel"
                                  :loading="loadingConturi"
                                  :max-height="210"
                                  placeholder="Selectează cont..."
                                  select-label=""
                                  selected-label=""
                                  deselect-label=""
                                  @select="(opt) => linie.cont_corespondent = opt.cod_complet"
                              >
                                  <template #option="props">
                                      <div class="d-flex justify-content-between">
                                          <span class="fw-bold">{{ props.option.cod_complet }}</span>
                                           <span class="text-muted small ms-2 text-truncate" style="max-width: 300px;">
                                               {{ props.option.descriere }}
                                           </span>
                                      </div>
                                  </template>
                              </multiselect>
                          </td>
                          <td>
                              <input
                                  type="text"
                                  class="form-control form-control-sm"
                                  v-model="linie.explicatie"
                                  placeholder="Explicație tranzacție..."
                              />
                          </td>
                          <td class="text-center">
                              <button
                                  type="button"
                                  class="btn btn-xs btn-light-danger"
                                  @click="liniiRJ.splice(idx, 1)"
                              >
                                  <i class="fa fa-trash"></i>
                              </button>
                          </td>
                      </tr>
                  </tbody>
              </table>

              <div class="d-flex justify-content-end">
                  <button
                      type="button"
                      class="btn btn-success"
                      :disabled="savingLine"
                      @click="saveNewLinii"
                  >
                      <span v-if="savingLine" class="spinner-border spinner-border-sm me-2"></span>
                      Salvează liniile noi
                  </button>
              </div>
          </div>
          <div v-else class="text-center py-3 text-muted border border-dashed rounded">
              Apasă pe butonul "Adaugă rând" pentru a introduce date noi manual.
          </div>
      </div>

      <div v-if="loadingLinii" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Se încarcă...</span>
        </div>
        <div class="text-muted mt-2">Se încarcă liniile extrasului...</div>
      </div>

      <!-- Tabel cu liniile extrasului selectat -->
      <div class="table-responsive" v-show="!loadingLinii">
        <table class="table table-bordered table-striped align-middle" id="tabel_linii_extras">
          <thead class="table-light">
            <tr class="text-uppercase fw-bold">
              <th style="width: 40px" class="text-center">#</th>
              <th style="width: 110px;">Data</th>
              <th>Explicație</th>
              <th style="width: 90px;">Tip</th>
              <th style="width: 90px;">Cont D</th>
              <th style="width: 90px;">Cont C</th>
              <th style="width: 110px;" class="text-end">Suma D</th>
              <th style="width: 110px;" class="text-end">Suma C</th>
              <th style="width: 80px;" class="text-center">RJ</th>
              <th style="width: 110px;" class="text-center">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(linie, index) in liniiExtras" :key="linie.id">
              <td class="text-center">{{ index + 1 }}</td>
              <td>{{ formatDate(linie.data_tranzactie) || '-' }}</td>
              <td>{{ linie.explicatie || '-' }}</td>
              <td>{{ linie.tip_tranzactie || '-' }}</td>
              <td>{{ linie.cont_debit || '-' }}</td>
              <td>{{ linie.cont_credit || '-' }}</td>
              <td class="text-end">{{ formatMoney(linie.suma_debit) }}</td>
              <td class="text-end">{{ formatMoney(linie.suma_credit) }}</td>
              <td class="text-center">
                <i
                  v-if="linie.include_in_registru"
                  class="fa fa-check text-success"
                  title="Inclus în registru"
                ></i>
                <i
                  v-else
                  class="fa fa-minus text-muted"
                  title="Nu este inclus în registru"
                ></i>
              </td>
              <td class="text-center">
              <button
                type="button"
                class="btn btn-sm btn-light-primary"
                :disabled="selectedExtras && selectedExtras.procesat"
                @click="setEditingLinie(linie)"
              >
                <i class="fa fa-pen-to-square me-1"></i>
                Editare
              </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
    <!-- Modal editare linie extras -->
    <div
      class="modal fade"
      id="modalEditLinieExtras"
      tabindex="-1"
      aria-labelledby="modalEditLinieExtrasLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="modalEditLinieExtrasLabel">
              Editare linie extras
            </h5>
            <button
              type="button"
              class="btn-close"
              aria-label="Close"
              @click="closeEditModal"
            ></button>
          </div>
          <div class="modal-body">
            <div v-if="!editingLinie" class="text-muted">
              Nu este selectată nicio linie pentru editare.
            </div>
            <div v-else>
              <div class="mb-3">
                <small class="text-muted">ID linie:</small>
                <div><strong>{{ editingLinie.id }}</strong></div>
              </div>

              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label">Data tranzacție</label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="editingLinie.data_tranzactie"
                  />
                </div>
                <div class="col-md-4">
                  <label class="form-label">Tip tranzacție</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="editingLinie.tip_tranzactie"
                  />
                </div>

                <div class="col-md-6">
                  <label class="form-label">Suma D</label>
                  <input
                    type="number"
                    step="0.01"
                    class="form-control"
                    v-model.number="editingLinie.suma_debit"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Cont D (Multiselect)</label>
                  <multiselect
                      v-model="editingLinie.cont_debit_obj"
                      :options="listaConturi"
                      track-by="id"
                      label="cod_complet"
                      :custom-label="contLabel"
                      placeholder="Selectează cont..."
                      :searchable="true"
                      :loading="loadingConturi"
                      :max-height="210"
                      select-label=""
                      selected-label=""
                      deselect-label=""
                      @select="(opt) => editingLinie.cont_debit = opt.cod_complet"
                  >
                  </multiselect>
                </div>

                <div class="col-md-6">
                  <label class="form-label">Suma C</label>
                  <input
                    type="number"
                    step="0.01"
                    class="form-control"
                    v-model.number="editingLinie.suma_credit"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Cont C (Multiselect)</label>
                  <multiselect
                      v-model="editingLinie.cont_credit_obj"
                      :options="listaConturi"
                      track-by="id"
                      label="cod_complet"
                      :custom-label="contLabel"
                      placeholder="Selectează cont..."
                      :searchable="true"
                      :loading="loadingConturi"
                      :max-height="210"
                      select-label=""
                      selected-label=""
                      deselect-label=""
                      @select="(opt) => editingLinie.cont_credit = opt.cod_complet"
                  >
                  </multiselect>
                </div>

                <div class="col-12">
                  <label class="form-label">Explicație</label>
                  <textarea
                    class="form-control"
                    rows="3"
                    v-model="editingLinie.explicatie"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer flex-column align-items-stretch align-items-md-center">
            <div class="d-flex justify-content-end w-100">
              <button
                type="button"
                class="btn btn-light me-2"
                @click="closeEditModal"
              >
                Închide
              </button>
              <button
                type="button"
                class="btn btn-success me-2"
                :disabled="savingRJ || !editingLinie || selectedExtras?.procesat || editingLinie?.include_in_registru"
                @click="triggerTrimiteRJ"
              >
                Trimite în RJ
              </button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="savingEdit || !editingLinie || selectedExtras?.procesat || editingLinie?.include_in_registru"
                @click="saveEditingLinie"
              >
                <span
                  v-if="savingEdit"
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Salvează modificările
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Multiselect from 'vue-multiselect'

export default {
  name: 'ExtraseBancare',
  props: {
    firmaId: {
      type: [Number, String],
      required: true,
    },
    // opțional: dacă vrei să filtrezi extrasele după fișierul de upload
    fisierId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      extrase: [],
      loading: false,
      selectedExtras: null,
      liniiExtras: [],
      loadingLinii: false,
      editingLinie: null,
      savingEdit: false,
      savingRJ: false,
      listaConturi: [],
      loadingConturi: false,
      liniiRJ: [],
      savingLine: false,
      showAddLines: false,
    }
  },
  components: {
    Multiselect
  },
  watch: {
    firmaId: {
      immediate: true,
      handler() {
        this.loadExtrase()
        this.loadConturi()
      },
    },
    fisierId() {
      this.loadExtrase()
    },
  },
  methods: {
    formatDate(d) {
      if (!d) return ''
      try {
        return new Date(d).toLocaleDateString('ro-RO')
      } catch (e) {
        return ''
      }
    },
    formatLunaAn(dataStart, dataEnd) {
      const value = dataStart || dataEnd
      if (!value) return ''

      try {
        const dt = new Date(value)
        if (isNaN(dt.getTime())) {
          return ''
        }
        // ex: "noiembrie 2025" -> "Noiembrie 2025"
        let text = dt.toLocaleDateString('ro-RO', {
          month: 'long',
          year: 'numeric',
        })
        if (text && text.length > 0) {
          text = text.charAt(0).toUpperCase() + text.slice(1)
        }
        return text
      } catch (e) {
        // fallback: formatare simplă de dată
        return this.formatDate(value)
      }
    },
    formatMoney(v) {
      const n = Number(v || 0)
      return (
        n.toLocaleString('ro-RO', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + ' lei'
      )
    },
    async loadLiniiExtras() {
      if (!this.selectedExtras) return
      this.loadingLinii = true

      try {
        const res = await axios.get('/api/administrare/work/extrase/detalii', {
          params: {
            extras_id: this.selectedExtras.id,
          },
        })
        const data = res.data
        // suportăm atât un array simplu, cât și { linii: [...] } sau { tranzactii: [...] }
        this.liniiExtras = Array.isArray(data)
          ? data
          : data.linii || data.tranzactii || []

        // (re)inițializăm DataTables după ce DOM-ul e updatat
        this.$nextTick(() => {
          this.initDataTableLinii()
        })
      } catch (e) {
        console.error('Eroare la încărcarea liniilor extrasului:', e)
        this.liniiExtras = []
        const msg =
          e?.response?.data?.error ||
          'Nu s-au putut încărca liniile pentru acest extras.'
        if (window.Swal) {
          window.Swal.fire({
            icon: 'error',
            title: 'Eroare',
            text: msg,
          })
        } else if (window.toastr) {
          window.toastr.error(msg)
        }
      }

      this.loadingLinii = false
    },
    initDataTableLinii() {
      // distrugem instanța anterioară dacă există
      if (this.dtLinii && this.dtLinii.destroy) {
        this.dtLinii.destroy()
        this.dtLinii = null
      }

      if (!window.$ || !window.$.fn || !window.$.fn.DataTable) {
        return
      }

      const $table = window.$('#tabel_linii_extras')
      if (!$table.length) return

      this.dtLinii = $table.DataTable({
        paging: true,
        searching: true,
        info: true,
        ordering: true,
        pageLength: 25,
        order: [[0, 'asc']],
        language: {
          url: "https://cdn.datatables.net/plug-ins/1.13.6/i18n/ro.json",
          search: "Caută:",
          lengthMenu: "Afișează _MENU_ înregistrări",
          info: "Afișate _START_ - _END_ din _TOTAL_ înregistrări",
          infoEmpty: "Nu sunt date",
          zeroRecords: "Nu am găsit nimic",
          paginate: {
            first: "Prima",
            last: "Ultima",
            next: "›",
            previous: "‹"
          }
        }
      })
    },
    initDataTableExtrase() {
      // distrugem instanța anterioară dacă există
      if (this.dtExtrase && this.dtExtrase.destroy) {
        this.dtExtrase.destroy()
        this.dtExtrase = null
      }

      if (!window.$ || !window.$.fn || !window.$.fn.DataTable) {
        return
      }

      const $table = window.$('#tabel_extrase_bancare')
      if (!$table.length) return

      this.dtExtrase = $table.DataTable({
        paging: true,
        searching: true,
        info: true,
        ordering: true,
        pageLength: 25,
        order: [[0, 'asc']],
        language: {
          url: 'https://cdn.datatables.net/plug-ins/1.13.6/i18n/ro.json',
          search: 'Caută:',
          lengthMenu: 'Afișează _MENU_ înregistrări',
          info: 'Afișate _START_ - _END_ din _TOTAL_ înregistrări',
          infoEmpty: 'Nu sunt date',
          zeroRecords: 'Nu am găsit nimic',
          paginate: {
            first: 'Prima',
            last: 'Ultima',
            next: '›',
            previous: '‹',
          },
        },
      })
    },
    async loadExtrase() {
      if (!this.firmaId) return
      this.loading = true
      try {
        const res = await axios.get('/api/administrare/work/extrase/list', {
          params: {
            firma_id: this.firmaId,
            fisier_id: this.fisierId || null,
          },
        })
        const data = res.data
        this.extrase = Array.isArray(data) ? data : data.extrase || []

        this.$nextTick(() => {
          this.initDataTableExtrase()
        })
      } catch (e) {
        console.error('Eroare la încărcarea extraselor:', e)
        this.extrase = []
        const msg =
          e?.response?.data?.error || 'Nu s-au putut încărca extrasele.'
        if (window.Swal) {
          window.Swal.fire({
            icon: 'error',
            title: 'Eroare',
            text: msg,
          })
        } else if (window.toastr) {
          window.toastr.error(msg)
        }
      }
      this.loading = false
    },
    reload() {
      // distrugem DataTable pentru linii
      if (this.dtLinii && this.dtLinii.destroy) {
        this.dtLinii.destroy()
        this.dtLinii = null
      }

      // distrugem DataTable pentru extrase înainte de reîncărcare
      if (this.dtExtrase && this.dtExtrase.destroy) {
        this.dtExtrase.destroy()
        this.dtExtrase = null
      }

      this.selectedExtras = null
      this.liniiExtras = []
      this.liniiRJ = []

      this.loadExtrase()
    },
    selectExtras(extras) {
      this.selectedExtras = extras
      this.liniiExtras = []
      this.liniiRJ = []
      // încărcăm liniile pentru extrasul selectat
      this.$nextTick(() => {
        this.loadLiniiExtras()
      })
    },
    clearSelectedExtras() {
      this.selectedExtras = null
      this.liniiExtras = []
      this.liniiRJ = []

      // distrugem DataTable la revenirea la listă
      if (this.dtLinii && this.dtLinii.destroy) {
        this.dtLinii.destroy()
        this.dtLinii = null
      }
    },
    blurActiveElement() {
      if (typeof document !== 'undefined') {
        const el = document.activeElement
        if (el && typeof el.blur === 'function') {
          el.blur()
        }
      }
    },
    closeEditModal() {
      // scoatem focusul de pe butonul X sau orice element din modal
      this.blurActiveElement()

      if (window.$ && window.$.fn && window.$.fn.modal) {
        window.$('#modalEditLinieExtras').modal('hide')
      }
      this.editingLinie = null
    },
    setEditingLinie(linie) {
      // clonăm obiectul ca să nu modificăm direct lista
      const copie = { ...linie }

      if (copie.cont_debit) {
        copie.cont_debit_obj = this.listaConturi.find(c => c.cod_complet === copie.cont_debit) || null
      }
      if (copie.cont_credit) {
        copie.cont_credit_obj = this.listaConturi.find(c => c.cod_complet === copie.cont_credit) || null
      }

      this.editingLinie = copie

      // Deschidem modalul simplu, cu jQuery
        window.$('#modalEditLinieExtras').modal('show')
    },
    async saveEditingLinie() {
      if (!this.editingLinie || !this.selectedExtras) return

      this.savingEdit = true

      try {
        // reconstruim conturile din obiectele multiselect
        this.editingLinie.cont_debit = this.editingLinie.cont_debit_obj ? this.editingLinie.cont_debit_obj.cod_complet : this.editingLinie.cont_debit
        this.editingLinie.cont_credit = this.editingLinie.cont_credit_obj ? this.editingLinie.cont_credit_obj.cod_complet : this.editingLinie.cont_credit

        // actualizăm linia modificată în lista locală
        const idx = this.liniiExtras.findIndex(l => l.id === this.editingLinie.id)
        if (idx !== -1) {
          // clonăm ca să păstrăm reactivitatea curată
          this.liniiExtras.splice(idx, 1, { ...this.editingLinie })
        }

        // pregătim payload pentru salvarea tuturor liniilor
        const payload = {
          extras_id: this.selectedExtras.id,
          linii: this.liniiExtras.map(l => {
            return {
              id: l.id,
              data_tranzactie: l.data_tranzactie,
              explicatie: l.explicatie,
              suma_debit: l.suma_debit,
              suma_credit: l.suma_credit,
              tip_tranzactie: l.tip_tranzactie,
              cont_debit: l.cont_debit,
              cont_credit: l.cont_credit,
              include_in_registru: l.include_in_registru ? 1 : 0,
            }
          }),
        }

        const res = await axios.post(
          '/api/administrare/work/extrase/salveaza-linii',
          payload
        )

        const okMsg = res.data?.message || 'Linia a fost salvată cu succes.'
        if (window.Swal) {
          window.Swal.fire({
            icon: 'success',
            title: 'Succes',
            text: okMsg,
            timer: 2000,
            showConfirmButton: false,
          })
        } else if (window.toastr) {
          window.toastr.success(okMsg)
        }

        // reîncărcăm liniile din backend ca să fim siguri că suntem în sync
        await this.loadLiniiExtras()
        this.closeEditModal()
      } catch (e) {
        console.error('Eroare la salvarea liniei extrasului:', e)
        const msg =
          e?.response?.data?.error ||
          'A apărut o eroare la salvarea liniei extrasului.'
        if (window.Swal) {
          window.Swal.fire({
            icon: 'error',
            title: 'Eroare',
            text: msg,
          })
        } else if (window.toastr) {
          window.toastr.error(msg)
        }
      }

      this.savingEdit = false
    },
    async triggerTrimiteRJ() {
      if (!this.editingLinie || !this.selectedExtras) return

      this.blurActiveElement()

      if (window.Swal) {
        const result = await window.Swal.fire({
          icon: 'warning',
          title: 'Trimite în Registru Jurnal',
          text: 'Odată trimisă în Registru Jurnal, linia nu va mai putea fi modificată. Continuați?',
          showCancelButton: true,
          confirmButtonText: 'Da, trimite',
          cancelButtonText: 'Renunță',
        })

        if (!result.isConfirmed) {
          return
        }
      } else {
        // fallback simplu
        if (!window.confirm('Odată trimisă în Registru Jurnal, linia nu va mai putea fi modificată. Continuați?')) {
          return
        }
      }

      // dacă utilizatorul confirmă, apelăm direct confirmTrimiteRJ
      this.confirmTrimiteRJ()
    },
    async confirmTrimiteRJ() {
      if (!this.editingLinie || !this.selectedExtras) return

      this.savingRJ = true

      try {
        // NU mai setăm include_in_registru aici.
        // this.editingLinie.include_in_registru = true

        // propagăm modificările (conturi, sume, explicație) în lista locală
        const idx = this.liniiExtras.findIndex(l => l.id === this.editingLinie.id)
        if (idx !== -1) {
          this.liniiExtras.splice(idx, 1, { ...this.editingLinie })
        }

        // salvăm toate liniile (fără să „forțăm” include_in_registru)
        const payloadLinii = {
          extras_id: this.selectedExtras.id,
          linii: this.liniiExtras.map(l => {
            const contDebitBaza = (l.cont_debit_baza || l.cont_debit || '').toString().trim()
            const contDebitAnalitic = (l.cont_debit_analitic || '').toString().trim()
            const contCreditBaza = (l.cont_credit_baza || l.cont_credit || '').toString().trim()
            const contCreditAnalitic = (l.cont_credit_analitic || '').toString().trim()

            const cont_debit_full =
              contDebitBaza !== ''
                ? (contDebitAnalitic !== '' ? `${contDebitBaza}.${contDebitAnalitic}` : contDebitBaza)
                : null

            const cont_credit_full =
              contCreditBaza !== ''
                ? (contCreditAnalitic !== '' ? `${contCreditBaza}.${contCreditAnalitic}` : contCreditBaza)
                : null

            return {
              id: l.id,
              data_tranzactie: l.data_tranzactie,
              explicatie: l.explicatie,
              suma_debit: l.suma_debit,
              suma_credit: l.suma_credit,
              tip_tranzactie: l.tip_tranzactie,
              cont_debit: cont_debit_full,
              cont_credit: cont_credit_full,
              include_in_registru: l.include_in_registru ? 1 : 0, // rămâne cum e în memorie
            }
          }),
        }

        await axios.post(
          '/api/administrare/work/extrase/salveaza-linii',
          payloadLinii
        )

        // procesăm DOAR linia curentă (linie_id)
        const resProc = await axios.post(
          '/api/administrare/work/extrase/proceseaza',
          {
            firma_id: this.firmaId,
            extras_id: this.selectedExtras.id,
            linie_id: this.editingLinie.id,
          }
        )

        const okMsg =
          resProc.data?.message ||
          'Linia a fost trimisă în Registru Jurnal.'
        if (window.Swal) {
          window.Swal.fire({
            icon: 'success',
            title: 'Succes',
            text: okMsg,
            timer: 2000,
            showConfirmButton: false,
          })
        } else if (window.toastr) {
          window.toastr.success(okMsg)
        }

        // reîncărcăm extrase + linii ca să vedem include_in_registru = 1 doar pe linia trimisă
        await this.loadExtrase()
        if (this.selectedExtras && this.selectedExtras.id) {
          const found = this.extrase.find(
            (e) => e.id === this.selectedExtras.id
          )
          if (found) {
            this.selectedExtras = found
          }
        }

        await this.loadLiniiExtras()
        this.closeEditModal()
      } catch (e) {
        console.error('Eroare la trimiterea în Registru Jurnal:', e)
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          'A apărut o eroare la trimiterea în Registru Jurnal.'
        if (window.Swal) {
          window.Swal.fire({
            icon: 'error',
            title: 'Eroare',
            text: msg,
          })
        } else if (window.toastr) {
          window.toastr.error(msg)
        }
      }

      this.savingRJ = false
    },

    contLabel(option) {
        return `${option.cod_complet} - ${option.descriere || ''}`
    },
    async loadConturi() {
        if (!this.firmaId) return;
        this.loadingConturi = true;
        try {
            const res = await axios.get('/api/contabilitate/plan_conturi', {
                params: { id_firma: this.firmaId }
            });
            this.listaConturi = res.data || [];
        } catch (e) {
            console.error(e);
        }
        this.loadingConturi = false;
    },
    dateAzi() {
        return new Date().toISOString().slice(0, 10);
    },
    openNativePicker(refName) {
        const input = this.$refs[refName]
        if (!input) return
        const el = Array.isArray(input) ? input[0] : input
        if (el && typeof el.showPicker === 'function') {
            el.showPicker()
        } else if (el) {
            el.focus()
            el.click()
        }
    },
    async saveNewLinii() {
        if (!this.selectedExtras || !this.liniiRJ.length) return;
        this.savingLine = true;
        
        try {
            for (const linie of this.liniiRJ) {
                const bancaCont = this.selectedExtras.banca;
                let contDebit = null;
                let contCredit = null;
                let sumaDebit = linie.suma_debit || 0;
                let sumaCredit = linie.suma_credit || 0;
                
                if (sumaDebit > 0) {
                    contDebit = bancaCont;
                    contCredit = linie.cont_corespondent_obj ? linie.cont_corespondent_obj.cod_complet : linie.cont_corespondent;
                } else if (sumaCredit > 0) {
                    contCredit = bancaCont;
                    contDebit = linie.cont_corespondent_obj ? linie.cont_corespondent_obj.cod_complet : linie.cont_corespondent;
                }

                const payload = {
                  extras_id: this.selectedExtras.id,
                  data_tranzactie: linie.data_tranzactie || null,
                  explicatie: linie.explicatie || null,
                  suma_debit: sumaDebit,
                  suma_credit: sumaCredit,
                  cont_debit: contDebit,
                  cont_credit: contCredit,
                };

                await axios.post('/api/administrare/work/extrase/adauga-linie', payload);
            }

            if (window.Swal) {
                window.Swal.fire({
                    position: 'top-end',
                    toast: true,
                    showConfirmButton: false,
                    timer: 2000,
                    icon: 'success',
                    title: 'Succes',
                    text: 'Liniile au fost adăugate.',
                });
            }

            this.liniiRJ = [];
            await this.loadLiniiExtras();
        } catch (e) {
            console.error(e);
            alert('Eroare la adăugarea liniilor.');
        }
        this.savingLine = false;
    },
  },
}
</script>

<style scoped>
#tabel_extrase_bancare {
  width: 100% !important;
}

/* =====================
   Vue-Multiselect tuning
   ===================== */
:deep(.multiselect) {
  min-height: 31px;
  font-size: 1rem !important;
  font-weight: 600 !important;
}
:deep(.multiselect__tags) {
  min-height: 31px;
  padding: 2px 30px 2px 6px;
  border-radius: 0.25rem;
  background-color: var(--bs-body-bg) !important;
  color: var(--bs-body-color) !important;
  border: 1px solid var(--bs-border-color, #e4e6ef) !important;
}
:deep(.multiselect__select) {
  height: 31px;
  top: 0;
}
:deep(.multiselect__single),
:deep(.multiselect__input),
:deep(.multiselect__placeholder) {
  font-size: 1rem !important;
  line-height: normal;
  background: transparent !important;
  color: var(--bs-body-color) !important;
  font-weight: 600 !important;
}
:deep(.multiselect__option) {
  font-size: 1rem !important;
  padding: 4px 8px;
  font-weight: 600 !important;
}
:deep(.multiselect__option--highlight),
:deep(.multiselect__option--selected.multiselect__option--highlight) {
  background: var(--bs-primary, #3e97ff) !important;
  color: #fff !important;
}
:deep(.multiselect__option--highlight .text-muted) {
  color: #fff !important;
}
:deep(.multiselect__content-wrapper) {
  z-index: 9999 !important;
  max-height: 210px !important;
}

/* Permiterea dropdown-ului să iasă din containerul responsive al tabelului */
.table-responsive {
    overflow: visible !important;
}

@media (max-width: 767.98px) {
    .table-responsive {
        overflow-x: auto !important;
    }
}

.border-dashed {
    border-style: dashed !important;
}

.btn-primary-bright {
    background-color: #009ef7 !important;
    color: #fff !important;
    border: none;
    transition: background-color 0.2s ease;
}

.btn-primary-bright:hover {
    background-color: #00b2ff !important;
    color: #fff !important;
}

.btn-primary-bright:active {
    background-color: #008cdb !important;
}
</style>
<style src="vue-multiselect/dist/vue-multiselect.css"></style>